import { useCallback, useEffect, useState } from "react";

/**
 * `P0-UIK-021`, MOVED to `app-shell` in round 203: **the browser half of the
 * router, and it is deliberately small.**
 *
 * It lived in `apps/console-web/src/router.ts` and was the console's alone. The
 * client shell needed exactly it, and a second copy is how two apps come to
 * disagree about what the back button does. `DECISION ADDED A35` already put
 * *"the router factory and its route guards"* in this package; this is that
 * sentence honoured rather than a new boundary.
 *
 * `routing.ts` is the PURE half , a path and a whitelist to a screen or a
 * redirect. This is the impure half: it reads `location` and changes it.
 *
 * **It cooperates with the SPA fallback rather than fighting it.**
 * `P0-INF-032` configured CloudFront to answer `index.html` with a `200` for
 * any unknown path, because a single-page application routes in the browser. So
 * a person who reloads on `/profil`, or types it, gets this application and
 * this module reads the path , there is no server route to add and none to
 * miss. `resolveRoute` already turns a path and a whitelist into a screen or a
 * redirect; this supplies the path and performs the navigation.
 *
 * **No routing dependency.** The two things a router must do here are read the
 * location and change it without a reload, and the platform does both. A
 * library would add a runtime dependency, a bundle, and an opinion, to replace
 * fifteen lines that each shell already tells what to render.
 */

export interface Location {
  readonly path: string;
  readonly search: string;
  readonly naviguer: (to: string) => void;
}

const read = (): { path: string; search: string } => ({
  path: globalThis.location.pathname,
  search: globalThis.location.search,
});

export function useLocation(): Location {
  const [here, setHere] = useState(read);

  useEffect(() => {
    // The back button is a navigation like any other, and an application that
    // ignores it is one a person cannot leave.
    const onPop = (): void => {
      setHere(read());
    };
    globalThis.addEventListener("popstate", onPop);
    return () => {
      globalThis.removeEventListener("popstate", onPop);
    };
  }, []);

  const naviguer = useCallback((to: string): void => {
    globalThis.history.pushState(null, "", to);
    setHere(read());
  }, []);

  return { path: here.path, search: here.search, naviguer };
}
